// components/ImageEditor/AIImageEditorModal.tsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/utils/cn";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { imageActionsService } from "@/core/MediaItem/ImageActionsService";
import { apiClient } from "@/services/apiClient";

interface AIImageEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  mediaItem: MediaItem | null;
  clientId: string;
}

interface EditorMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  imageBase64?: string;
  mimeType?: string;
  timestamp: Date;
}

export const AIImageEditorModal: React.FC<AIImageEditorModalProps> = ({
  isOpen,
  onClose,
  mediaItem,
  clientId,
}) => {
  const [messages, setMessages] = useState<EditorMessage[]>([]);
  const [currentImageBase64, setCurrentImageBase64] = useState<string | null>(null);
  const [currentMimeType, setCurrentMimeType] = useState<string>("image/jpeg");
  const [originalImageBase64, setOriginalImageBase64] = useState<string | null>(null);
  const [originalMimeType, setOriginalMimeType] = useState<string>("image/jpeg");
  const [isPreviewingOriginal, setIsPreviewingOriginal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingOriginal, setIsLoadingOriginal] = useState(false);
  const [isSavingRevision, setIsSavingRevision] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState("");

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const hasEdits = messages.some((m) => m.role === "assistant" && m.imageBase64 && m.id !== "original");
  const isUsingOriginal = currentImageBase64 === originalImageBase64;

  // Scroll to bottom when new messages arrive
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isGenerating) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, isGenerating, onClose]);

  // Load original image when modal opens
  useEffect(() => {
    const loadOriginalImage = async () => {
      if (!isOpen || !mediaItem || !clientId) return;

      setIsLoadingOriginal(true);
      setError(null);
      setMessages([]);
      setCurrentImageBase64(null);
      setOriginalImageBase64(null);
      setIsPreviewingOriginal(false);

      try {
        // Use format=base64 to have the backend proxy the image bytes and
        // return base64 directly. This avoids CORS issues that occur when
        // the browser tries to fetch() a GCS signed URL directly.
        const endpoint = `/client/${clientId}/image/${mediaItem.id}?format=base64`;
        const response = await apiClient.get<{ base64: string; mimeType: string }>(endpoint);

        const base64 = response.base64;
        const mimeType = response.mimeType || "image/webp";

        setCurrentImageBase64(base64);
        setCurrentMimeType(mimeType);
        setOriginalImageBase64(base64);
        setOriginalMimeType(mimeType);
        setIsLoadingOriginal(false);

        // Seed the chat with the original image
        setMessages([{
          id: "original",
          role: "assistant",
          content: "Original image loaded.",
          imageBase64: base64,
          mimeType: mimeType,
          timestamp: new Date(),
        }]);
      } catch (err) {
        console.error("Error loading image:", err);
        setError(err instanceof Error ? err.message : "Failed to load image");
        setIsLoadingOriginal(false);
      }
    };

    loadOriginalImage();
  }, [isOpen, mediaItem, clientId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setMessages([]);
      setCurrentImageBase64(null);
      setOriginalImageBase64(null);
      setInputValue("");
      setError(null);
      setIsGenerating(false);
      setIsPreviewingOriginal(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const prompt = inputValue.trim();
    if (!prompt || !currentImageBase64 || !mediaItem || isGenerating) return;

    // Stop previewing original when submitting
    setIsPreviewingOriginal(false);

    setInputValue("");
    setError(null);

    const userMessage: EditorMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: prompt,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);

    setIsGenerating(true);

    try {
      // Single-turn: send current image + prompt only.
      // The current image already reflects all prior edits,
      // so conversation history is unnecessary and degrades
      // quality due to missing thought signatures.
      const result = await imageActionsService.editImageWithAI(
        clientId,
        mediaItem.id,
        currentImageBase64,
        prompt,
        currentMimeType,
        undefined,
        mediaItem.getAspectRatioCategory() !== "unknown" ? mediaItem.getAspectRatioCategory() : undefined,
      );

      if (result.success && result.imageBase64) {
        const assistantMessage: EditorMessage = {
          id: `assistant-${Date.now()}`,
          role: "assistant",
          content: result.text || "Here is the edited image.",
          imageBase64: result.imageBase64,
          mimeType: result.mimeType,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, assistantMessage]);

        setCurrentImageBase64(result.imageBase64);
        setCurrentMimeType(result.mimeType || "image/png");
      } else {
        setError(result.error || "Failed to generate edited image");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!currentImageBase64 || !mediaItem) return;

    const link = document.createElement("a");
    link.href = `data:${currentMimeType};base64,${currentImageBase64}`;
    const extension = currentMimeType.split("/")[1] || "png";
    const baseName = mediaItem.originalFilename.replace(/\.[^/.]+$/, "");
    link.download = `${baseName}_edited.${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSaveAsRevision = async () => {
    if (!currentImageBase64 || !mediaItem || messages.length === 0) return;

    setIsSavingRevision(true);
    setError(null);

    try {
      const result = await imageActionsService.uploadRevisionComplete(
        clientId,
        mediaItem.id,
        mediaItem.fileIdentity,
        {
          base64: currentImageBase64,
          mimeType: currentMimeType,
          originalFilename: mediaItem.originalFilename,
        }
      );

      if (result.success) {
        onClose();
      } else {
        setError(result.error || "Failed to save revision");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save revision");
    } finally {
      setIsSavingRevision(false);
    }
  };

  const handleUseOriginal = () => {
    if (!originalImageBase64) return;
    setCurrentImageBase64(originalImageBase64);
    setCurrentMimeType(originalMimeType);
    setIsPreviewingOriginal(false);
  };

  const handleReset = () => {
    setError(null);
    setIsPreviewingOriginal(false);
    if (originalImageBase64) {
      setCurrentImageBase64(originalImageBase64);
      setCurrentMimeType(originalMimeType);
      // Re-seed with just the original image
      setMessages([{
        id: "original",
        role: "assistant",
        content: "Original image loaded.",
        imageBase64: originalImageBase64,
        mimeType: originalMimeType,
        timestamp: new Date(),
      }]);
    }
  };

  // The image shown in the main preview area
  const displayBase64 = isPreviewingOriginal ? originalImageBase64 : currentImageBase64;
  const displayMimeType = isPreviewingOriginal ? originalMimeType : currentMimeType;

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-xs"
        onClick={() => !isGenerating && onClose()}
      />

      {/* Modal */}
      <div
        className={cn(
          "relative bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden",
          "w-[95vw] h-[90vh] max-w-6xl",
          "animate-in fade-in-0 zoom-in-95 duration-200"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-gray-900">
              AI Image Editor
            </h2>
            {mediaItem && (
              <span className="text-sm text-gray-500 truncate max-w-[200px]">
                {mediaItem.originalFilename}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {hasEdits && (
              <button
                onClick={handleReset}
                disabled={isGenerating}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
              >
                Reset All
              </button>
            )}
            <button
              onClick={handleDownload}
              disabled={!currentImageBase64 || isGenerating || isSavingRevision}
              className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Download
            </button>
            {hasEdits && (
              <button
                onClick={handleSaveAsRevision}
                disabled={!currentImageBase64 || isGenerating || isSavingRevision || isUsingOriginal}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSavingRevision && (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                )}
                {isSavingRevision ? "Saving..." : "Save as Revision"}
              </button>
            )}
            <button
              onClick={onClose}
              disabled={isGenerating || isSavingRevision}
              className="p-1.5 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
              aria-label="Close modal"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col md:flex-row min-h-0">
          {/* Image preview - left side */}
          <div className="flex-1 md:w-3/5 p-4 flex flex-col items-center justify-center bg-gray-100 relative">
            {isLoadingOriginal ? (
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                <p className="text-gray-600">Loading image...</p>
              </div>
            ) : displayBase64 ? (
              <>
                <div className="relative max-w-full max-h-full flex-1 flex items-center justify-center">
                  <img
                    src={`data:${displayMimeType};base64,${displayBase64}`}
                    alt={isPreviewingOriginal ? "Original" : "Current edit"}
                    className="max-w-full max-h-[calc(90vh-240px)] object-contain rounded-lg shadow-lg"
                  />
                  {isPreviewingOriginal && (
                    <div className="absolute top-3 left-3 bg-black/60 text-white text-xs px-2 py-1 rounded-sm">
                      Original
                    </div>
                  )}
                  {isGenerating && (
                    <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-lg">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        <p className="text-gray-700 font-medium">Generating...</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Image controls bar */}
                {hasEdits && !isGenerating && (
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      onMouseDown={() => setIsPreviewingOriginal(true)}
                      onMouseUp={() => setIsPreviewingOriginal(false)}
                      onMouseLeave={() => setIsPreviewingOriginal(false)}
                      onTouchStart={() => setIsPreviewingOriginal(true)}
                      onTouchEnd={() => setIsPreviewingOriginal(false)}
                      className={cn(
                        "px-3 py-1.5 text-xs rounded-md transition-colors",
                        isPreviewingOriginal
                          ? "bg-gray-800 text-white"
                          : "bg-white text-gray-600 hover:bg-gray-200 border border-gray-300"
                      )}
                    >
                      Hold to compare original
                    </button>
                    {!isUsingOriginal && (
                      <button
                        onClick={handleUseOriginal}
                        className="px-3 py-1.5 text-xs bg-white text-gray-600 hover:bg-gray-200 border border-gray-300 rounded-md transition-colors"
                      >
                        Edit from original
                      </button>
                    )}
                    {isUsingOriginal && messages.length > 0 && (
                      <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-sm">
                        Working from original
                      </span>
                    )}
                  </div>
                )}
              </>
            ) : (
              <p className="text-gray-500">No image selected</p>
            )}
          </div>

          {/* Chat panel - right side */}
          <div className="md:w-2/5 flex flex-col border-l bg-white min-h-0">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <div key={message.id}>
                  {message.id === "original" ? (
                    // Original image entry
                    <div className="flex flex-col items-start gap-2">
                      <div className="max-w-[85%]">
                        <img
                          src={`data:${message.mimeType || "image/jpeg"};base64,${message.imageBase64}`}
                          alt="Original"
                          className={cn(
                            "rounded-lg shadow-md max-h-40 object-contain cursor-pointer hover:opacity-90 transition-all",
                            currentImageBase64 === message.imageBase64
                              ? "ring-2 ring-blue-500"
                              : ""
                          )}
                          onClick={() => {
                            setCurrentImageBase64(message.imageBase64!);
                            setCurrentMimeType(message.mimeType || "image/jpeg");
                            setIsPreviewingOriginal(false);
                          }}
                        />
                        <p className="text-xs text-gray-400 mt-1">Original</p>
                      </div>
                      {!hasEdits && (
                        <div className="text-gray-500 mt-2">
                          <p className="text-sm font-medium">Describe your edit</p>
                          <div className="mt-2 text-xs text-gray-400 space-y-1">
                            <p>"Add warm golden hour lighting"</p>
                            <p>"Add the text 'Brunch Special' in elegant font"</p>
                            <p>"Turn this into an abstract watercolor painting"</p>
                            <p>"Make it pop art style with bold colors"</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    // User and assistant messages
                    <div
                      className={cn(
                        "flex flex-col gap-2",
                        message.role === "user" ? "items-end" : "items-start"
                      )}
                    >
                      <div
                        className={cn(
                          "max-w-[85%] rounded-lg px-4 py-2",
                          message.role === "user"
                            ? "bg-blue-600 text-white"
                            : "bg-gray-100 text-gray-900"
                        )}
                      >
                        <p className="text-sm">{message.content}</p>
                      </div>
                      {message.imageBase64 && (
                        <div className="max-w-[85%]">
                          <img
                            src={`data:${message.mimeType || "image/png"};base64,${message.imageBase64}`}
                            alt="Generated"
                            className={cn(
                              "rounded-lg shadow-md max-h-32 object-contain cursor-pointer hover:opacity-90 transition-all",
                              currentImageBase64 === message.imageBase64
                                ? "ring-2 ring-blue-500"
                                : ""
                            )}
                            onClick={() => {
                              setCurrentImageBase64(message.imageBase64!);
                              setCurrentMimeType(message.mimeType || "image/png");
                              setIsPreviewingOriginal(false);
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form
              onSubmit={handleSubmit}
              className="border-t p-4 bg-gray-50"
            >
              <div className="flex gap-2">
                <textarea
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit(e);
                    }
                  }}
                  placeholder="Describe your edit..."
                  disabled={isGenerating || !currentImageBase64}
                  className="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  rows={2}
                />
                <button
                  type="submit"
                  disabled={!inputValue.trim() || isGenerating || !currentImageBase64}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed self-end"
                >
                  {isGenerating ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
